import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { ILogEmailUIProps, IRelationOption } from "../interface";
import { LogEmailBodyRespone, ZomentumEntities } from "@/Contracts";
import FormBuilder from "antd-form-builder";
import { getNearestPastFiveMinutes, isVendorApp } from "@/Utils";
import range from "lodash/range";
import ZButton from "@atoms/ZButton";
import "../index.less";
import ReactShadow from "react-shadow";
import ZLogEmailBodyDrawer from "./ZLogEmailBodyDrawer";
import ZEmailAttachmentList from "./ZEmailAttachmentList";
import TextArea from "antd/lib/input/TextArea";
import { getLogEmailBody } from "../../../../services/SalesActivity";
import { ZIcon, ZSelect, ZTag, ZTooltip } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import ZSpin from "@molecules/ZSpin";
import "./index.less";

const ZAddLogEmail: React.FC<ILogEmailUIProps> = ({
	onSubmit,
	onClose,
	form,
	isLoading = false,
	ZOpportunitySelectFormItem,
	ZClientSelectFormItem,
	ZUserSelectFormItem,
	isSubmitDisabled = false,
	currentActiveSalesActivityId,
	subject,
	isEmailLogBodyAvailable,
	ZVendorPartnerSelectFormItem,
	ZVendorOpportunitySelectFormItem,
	ZVendorClientSelectFormItem,
	datePattern,
	systemEmail,
	handleFormChange
}) => {
	const isVendorApplication = isVendorApp();
	const currentDate = new Date();
	const todayStartHour = currentDate.getHours();
	const todayStartMinute = currentDate.getMinutes();
	const todayStartSecond = currentDate.getSeconds();
	const startDate = new Date(new Date().setHours(0, 0, 0, 0));
	const todayStartDateUnixTimeStamp = startDate.getTime();
	const isSystemEmail = !!systemEmail;

	const [loading, setLoading] = useState<boolean>(false);

	const [logEmailBodyResponse, setLogEmailBodyResponse] =
		useState<LogEmailBodyRespone | null>(null);

	const getEmailBody = async () => {
		if (!currentActiveSalesActivityId || !isEmailLogBodyAvailable) {
			return;
		}
		try {
			setLoading(true);
			setLogEmailBodyResponse(
				await getLogEmailBody(currentActiveSalesActivityId)
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getEmailBody();
	}, []);

	const getRelationOptions = useCallback(() => {
		const relationOptions: IRelationOption[] = [];

		if (isVendorApplication) {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.VendorOpportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorOpportunitySelectFormItem />
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.VendorClient,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorClientSelectFormItem />
					</div>
				)
			});
			relationOptions.push({
				label: "Partner",
				value: ZomentumEntities.VendorCompany,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZVendorPartnerSelectFormItem />
					</div>
				)
			});
		} else {
			relationOptions.push({
				label: "Opportunity",
				value: ZomentumEntities.Opportunity,
				datatestid: "opportunity-radio",
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZOpportunitySelectFormItem />
					</div>
				)
			});
			relationOptions.push({
				label: "Client",
				value: ZomentumEntities.Client,
				children: (
					<div className="mt-3 sales-activity-relation-options">
						<ZClientSelectFormItem />
					</div>
				)
			});
		}
		return relationOptions;
	}, [
		isVendorApplication,
		ZVendorPartnerSelectFormItem,
		ZVendorClientSelectFormItem,
		ZOpportunitySelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZClientSelectFormItem
	]);

	const validateTime = (time: moment.Moment | null) => {
		if (!time) return;

		time.isAfter(getNearestPastFiveMinutes(currentDate))
			? form.setFieldsValue({
					time: getNearestPastFiveMinutes(currentDate)
			  })
			: form.setFieldsValue({
					time: getNearestPastFiveMinutes(time.toDate())
			  });
	};

	const onTimeChangeHandler = (newValue: moment.Moment | null) => {
		validateTime(newValue);
	};

	const DateAndTime = () => (
		<FormBuilder
			form={form}
			disabled={isLoading}
			meta={{
				columns: 2,
				fields: [
					{
						key: "date",
						required: true,
						message: "Please enter date",
						widget: EWidgets.DATEPICKER,
						wrapperCol: { span: 15 },
						widgetProps: {
							datePattern,
							allowClear: false,
							placeholder: "Date",
							className: "w-full",
							disabled: isSystemEmail,
							onChange: (
								date: moment.Moment | null,
								_dateString: string
							) => {
								const time = form.getFieldValue(
									"time"
								) as moment.Moment;
								if (date && time) {
									form.setFields([
										{
											name: "time",
											value:
												date.format("MM-DD-YYYY") ===
												moment(currentDate).format(
													"MM-DD-YYYY"
												)
													? getNearestPastFiveMinutes(
															currentDate
													  )
													: getNearestPastFiveMinutes(
															date.toDate()
													  )
										}
									]);
								}
							},
							disabledDate: (date: moment.Moment) =>
								date
									? date.toDate().getTime() >=
									  todayStartDateUnixTimeStamp +
											24 * 60 * 60 * 1000
									: false
						},
						hasFeedback: true,
						initialValue: moment()
					},
					{
						key: "time",
						widget: EWidgets.TIMEPICKER,
						wrapperCol: { span: 13 },
						required: true,
						message: "Please enter time",
						widgetProps: {
							minuteStep: 5,
							allowClear: false,
							format: "HH:mm",
							placeholder: "Time",
							className: "w-full",
							hideDisabledOptions: true,
							disabled: isSystemEmail,
							disabledHours: () => {
								const date = form.getFieldValue(
									"date"
								) as moment.Moment;
								return date &&
									date.unix() * 1000 >=
										todayStartDateUnixTimeStamp
									? range(todayStartHour + 1, 24, 1)
									: new Array<number>();
							},
							disabledMinutes: (selectedHour: number) => {
								const date = form.getFieldValue(
									"date"
								) as moment.Moment;
								return date &&
									date.unix() * 1000 >=
										todayStartDateUnixTimeStamp &&
									selectedHour >= todayStartHour
									? range(todayStartMinute, 60, 1)
									: new Array<number>();
							},
							disabledSeconds: (
								selectedHour: number,
								selectedMinute: number
							) => {
								const date = form.getFieldValue(
									"date"
								) as moment.Moment;
								return date &&
									date.unix() * 1000 >=
										todayStartDateUnixTimeStamp &&
									selectedHour >= todayStartHour &&
									selectedMinute >= todayStartMinute
									? range(todayStartSecond, 60, 1)
									: new Array<number>();
							},
							onOk: validateTime,
							onChange: onTimeChangeHandler
						},
						hasFeedback: true,
						initialValue: getNearestPastFiveMinutes(currentDate)
					}
				]
			}}
		/>
	);

	const [logEmailBodyDrawerOpen, setLogEmailBodyDrawerOpen] =
		useState<boolean>(false);

	const recipientOptions =
		logEmailBodyResponse?.email_person_details?.to?.map(
			(recipient, index) => {
				const email = recipient.email || "No Email";
				return {
					name: recipient.name
						? `${recipient.name} <${email}>`
						: email,
					value: email,
					key: index
				};
			}
		) ?? [];

	const toField = isSystemEmail
		? [
				{
					key: "recipients",
					label: "Recipients",
					widget: () => {
						const recipientTagRender = (props: any) => {
							const { label, value } = props;
							const isExternalEmail = label === value;
							return (
								<ZTag
									className={
										isExternalEmail
											? "external-email-recipient-tag"
											: ""
									}
									closable={false}
								>
									{label}
								</ZTag>
							);
						};

						return (
							<div className="flex items-center">
								<ZSelect
									mode="tags"
									selectOptions={recipientOptions}
									style={{ width: "98%" }}
									value={recipientOptions.map(
										({ value }) => value
									)}
									tagRender={recipientTagRender}
									disabled={true}
								/>
								<ZTooltip
									title="Each recipient received a separate email with resolved merge tags."
									placement="left"
								>
									<span>
										<ZIcon
											name={EIcons.INFOCIRCLE}
											colorVariant={6}
											size="sm"
											className="mx-2"
										/>
									</span>
								</ZTooltip>
							</div>
						);
					}
				}
		  ]
		: [];

	const emailBodyField = isSystemEmail
		? [
				{
					key: "message_body",
					render: () => {
						return (
							<div>
								<div
									className="email-thread-message-body-view"
									id={`email-thread-message-body-${currentActiveSalesActivityId}`}
								>
									<ReactShadow.div>
										<div
											dangerouslySetInnerHTML={{
												__html:
													logEmailBodyResponse?.email_log_body_html ??
													""
											}}
										></div>
									</ReactShadow.div>
								</div>
								{logEmailBodyResponse?.attachments && 
								 logEmailBodyResponse.attachments.length > 0 && (
									<ZEmailAttachmentList
										attachments={logEmailBodyResponse.attachments}
										emailLogId={currentActiveSalesActivityId || ""}
									/>
								)}
							</div>
						);
					}
				}
		  ]
		: [];

	const sections: Array<IFormSectionProps> = [
		{
			isIndentFormItems: false,
			hideDivider: true,
			meta: {
				formItemLayout: [5, 19],
				fields: [
					{
						key: "title",
						label: "Subject",
						widget: EWidgets.INPUT,
						widgetProps: {
							width: "full",
							placeholder: "Please enter subject",
							disabled: isSystemEmail
						},
						required: true,
						message: "Please enter subject",
						hasFeedback: true,
						rules: [
							{
								whitespace: true,
								message: "Title cannot be empty"
							}
						]
					},
					...toField,
					{
						key: "description",
						label: "Description",
						widget: ({ value, onChange }) => (
							<div>
								<TextArea
									placeholder="Please enter description"
									allowClear
									value={value}
									onChange={e =>
										onChange(e.currentTarget.value)
									}
									disabled={isSystemEmail}
									className="h-19 resize-none"
								/>
								{currentActiveSalesActivityId &&
									isEmailLogBodyAvailable &&
									!isSystemEmail && (
										<div className="text-right">
											<ZButton
												type="link"
												className="p-0"
												onClick={() =>
													setLogEmailBodyDrawerOpen(
														true
													)
												}
											>
												View Email
											</ZButton>
										</div>
									)}
							</div>
						)
					},
					{
						key: "custom_date",
						label: "Date",
						required: true,
						initialValue: moment(),
						widget: DateAndTime
					},
					{
						key: "relation",
						label: "Related to",
						widget: EWidgets.RADIOGROUP,
						widgetProps: {
							className: "sales-activity-relation-radio",
							space: "middle",
							options: getRelationOptions(),
							disabled: isSystemEmail
						},
						required: true,
						message: "Please select relation"
					},
					{
						key: "author_id",
						render: () => <ZUserSelectFormItem />
					},
					...emailBodyField
				]
			}
		}
	];

	const formProps: IFormProps = {
		key: "AddLogEmailForm",
		form,
		sections,
		disabled: isLoading,
		onFinish: onSubmit,
		submitButtonText: "Save",
		isRightFooter: true,
		isStickFooter: true,
		isCancelAllowed: true,
		onCancel: onClose,
		isSubmitDisabled: isSystemEmail || isSubmitDisabled,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange
	};

	return (
		<div className="z-log-email h-full">
			<ZSpin spinning={loading}>
				<ZForm {...formProps} className="py-4 h-full" />
			</ZSpin>
			{currentActiveSalesActivityId &&
				isEmailLogBodyAvailable &&
				!isSystemEmail && (
					<ZLogEmailBodyDrawer
						subject={subject ?? ""}
						visible={logEmailBodyDrawerOpen}
						onClose={() => setLogEmailBodyDrawerOpen(false)}
						currentActiveSalesActivityId={
							currentActiveSalesActivityId
						}
					/>
				)}
		</div>
	);
};

export default ZAddLogEmail;
